/**
 * FileTabStrip — IDE-style closeable file tabs (replaces `ArtifactTabs`, CR §3.4).
 *
 * `ArtifactTabs` rendered five permanent tabs, one per artifact, because every artifact was always
 * "present". F1 inverts that: the project chat is the home surface and artifacts are files you open,
 * so this strip shows a non-closeable chat tab plus only the files currently open.
 *
 * ── ACCESSIBILITY — this is the part that is NOT cosmetic ───────────────────────────────────────
 * The shared `ui/tabs` primitive marks up `role="tablist"`/`role="tab"` but implements no keyboard
 * model: every tab is in the tab sequence and arrow keys do nothing. That is wrong for a tablist and
 * it does not survive closeable tabs at all, so this component implements the full WAI-ARIA Tabs
 * pattern itself rather than extending the primitive:
 *
 *   • ArrowLeft / ArrowRight — move between tabs, wrapping at both ends, with automatic activation
 *     (focus and selection move together — correct here because switching tabs is cheap and has no
 *     side effects)
 *   • Home / End             — first / last tab
 *   • Delete / Backspace     — close the focused tab, per Track 3's accessibility findings. The
 *     chat tab is non-closeable and the key is a no-op there.
 *   • Roving tabindex        — exactly one tab is reachable by Tab; arrows navigate within the strip
 *     so the strip costs one Tab stop, not six
 *
 * Closing moves focus deliberately (see `useFileTabs.closeTab`) — never to `<body>`.
 *
 * Horizontal overflow scrolls rather than wrapping. `ArtifactTabs` passed `flex-wrap`, which with
 * closeable tabs makes rows reflow as tabs are closed and every remaining tab jump position.
 */

import { useCallback, useEffect, useRef } from 'react';
import { MessageSquare, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ARTIFACT_LABELS, type ArtifactType } from '@/hooks/useArtifacts';
import { CHAT_TAB, type FileTabId } from './useFileTabs';

export interface FileTabStripProps {
  openTabs: ArtifactType[];
  activeTab: FileTabId;
  onActivate: (tabId: FileTabId) => void;
  onClose: (artifactType: ArtifactType) => void;
  /**
   * Increment to pull DOM focus onto `focusTabId`. Used by `Phase0Page` to honour the focus target
   * returned by `closeTab` — a token rather than a direct ref call so the parent stays declarative.
   */
  focusToken?: number;
  focusTabId?: FileTabId;
  className?: string;
}

/** Tab ids in render order: chat first (home), then open files. */
function tabOrder(openTabs: ArtifactType[]): FileTabId[] {
  return [CHAT_TAB, ...openTabs];
}

export function FileTabStrip({
  openTabs,
  activeTab,
  onActivate,
  onClose,
  focusToken = 0,
  focusTabId,
  className,
}: FileTabStripProps): JSX.Element {
  const stripRef = useRef<HTMLDivElement>(null);
  const appliedFocusToken = useRef<number>(focusToken);

  const focusTab = useCallback((tabId: FileTabId): void => {
    const el = stripRef.current?.querySelector<HTMLElement>(`[data-tab-id="${tabId}"]`);
    el?.focus();
  }, []);

  // Honour an externally requested focus move (tab close). Guarded on the token so a re-render for
  // any other reason does not yank focus away from wherever the user has since moved it.
  useEffect(() => {
    if (focusToken === appliedFocusToken.current) return;
    appliedFocusToken.current = focusToken;
    if (focusTabId) focusTab(focusTabId);
  }, [focusToken, focusTabId, focusTab]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tabId: FileTabId): void => {
    const order = tabOrder(openTabs);
    const index = order.indexOf(tabId);
    if (index === -1) return;

    let next: FileTabId | undefined;
    switch (event.key) {
      case 'ArrowRight':
        next = order[(index + 1) % order.length];
        break;
      case 'ArrowLeft':
        next = order[(index - 1 + order.length) % order.length];
        break;
      case 'Home':
        next = order[0];
        break;
      case 'End':
        next = order[order.length - 1];
        break;
      case 'Delete':
      case 'Backspace':
        // The chat tab is the home surface and cannot be closed.
        if (tabId !== CHAT_TAB) {
          event.preventDefault();
          onClose(tabId);
        }
        return;
      default:
        return;
    }

    event.preventDefault();
    onActivate(next);
    focusTab(next);
  };

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label="Open files"
      aria-orientation="horizontal"
      className={cn(
        'flex shrink-0 items-stretch gap-1 overflow-x-auto border-b-[1.6px] border-outlines',
        className,
      )}
    >
      {tabOrder(openTabs).map((tabId) => {
        const isChat = tabId === CHAT_TAB;
        const isActive = activeTab === tabId;
        const label = isChat ? 'Project Chat' : (ARTIFACT_LABELS[tabId] ?? tabId);

        return (
          <div key={tabId} className="relative flex shrink-0 items-center">
            <button
              type="button"
              role="tab"
              id={`file-tab-${tabId}`}
              data-tab-id={tabId}
              aria-selected={isActive}
              aria-controls={`file-tabpanel-${tabId}`}
              // Roving tabindex: only the selected tab is in the Tab sequence.
              tabIndex={isActive ? 0 : -1}
              onClick={() => onActivate(tabId)}
              onKeyDown={(e) => handleKeyDown(e, tabId)}
              className={cn(
                'inline-flex max-w-[16rem] items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
                isChat ? 'pr-4' : 'pr-9',
                isActive
                  ? 'border-b-[1.6px] border-primary font-semibold text-primary-700'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {isChat && <MessageSquare className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              <span className="truncate">{label}</span>
            </button>

            {/* Close affordance. A sibling button rather than a nested one — a button inside a
                `role="tab"` button is invalid HTML and screen readers resolve it unpredictably.
                Keyboard users close with Delete/Backspace on the tab itself, so this control is
                removed from the tab sequence to avoid two stops per tab. */}
            {!isChat && (
              <button
                type="button"
                tabIndex={-1}
                aria-label={`Close ${label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(tabId);
                }}
                className="absolute right-2 flex h-5 w-5 items-center justify-center rounded text-text-secondary transition-colors hover:bg-outlines hover:text-text-primary"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FileTabStrip;
